<script setup lang="ts">
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { ref, defineEmits, computed } from 'vue'
  import { AiAugmentedByMapperKeysEnum as DatamapperKey } from '@/types/generated'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { Loader2 } from 'lucide-vue-next'
  import { storeToRefs } from 'pinia'
  import { useToast } from '@/components/ui/toast'
  import { yup } from '@/lib/validation'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'

  const emit = defineEmits(['close'])

  // Translations
  const { t } = useTranslation({
    en: {
      yearsOfEmployment: 'Years of Employment',
      totalGrossSalary: 'Total Gross Salary',
      factor: 'Factor',
      severanceSum: 'Severance Sum',
      startOfEmployment: 'Start of Employment',
      endOfEmployment: 'End of Employment',
      baseSalary: 'Base Salary',
      additionalComponents: 'Additional Components',
      save: 'Save',
      updatedFields: 'Updated Fields',
      severanceFieldsUpdated: 'Severance sum fields updated'
    },
    de: {
      yearsOfEmployment: 'Jahre Betriebszugehörigkeit',
      totalGrossSalary: 'Gesamtgehalt Brutto',
      factor: 'Faktor',
      severanceSum: 'Abfindungssumme',
      startOfEmployment: 'Arbeitsbeginn',
      endOfEmployment: 'Beendigungsdatum',
      baseSalary: 'Grundgehalt',
      additionalComponents: 'Zusätzliche Bestandteile',
      save: 'Speichern',
      updatedFields: 'Felder aktualisiert',
      severanceFieldsUpdated: 'Abfindungsfelder wurden aktualisiert'
    }
  })

  const caseStore = useCaseStore()
  const { isSavingCase } = storeToRefs(useCaseStore())
  const { trackEvent } = useMetrics()

  const startOfEmploymentDate = ref<string>('')
  const endOfEmploymentDate = ref<string>('')

  const yearsOfEmployment = computed(() => {
    try {
      if (!startOfEmploymentDate.value || !endOfEmploymentDate.value) {
        return 0
      }
      const [startMonth, startYear] = startOfEmploymentDate.value.split('.').map(Number)
      const [endMonth, endYear] = endOfEmploymentDate.value.split('.').map(Number)
      if (isNaN(startMonth) || isNaN(startYear) || isNaN(endMonth) || isNaN(endYear)) {
        return 0
      }
      const totalMonths = (endYear - startYear) * 12 + (endMonth - startMonth) + 1
      const totalYears = totalMonths / 12
      return Math.round(totalYears * 100) / 100 // rounded to 2 decimal places
    } catch (e) {
      return 0
    }
  })

  const baseSalary = ref<number>(0)
  const additionalComponents = ref<number>(0)

  const totalGrossSalary = computed(() => {
    try {
      return Number(baseSalary.value) + Number(additionalComponents.value)
    } catch (e) {
      return 0
    }
  })

  const factor = ref<number>(0.5)

  const severanceSum = computed(() => {
    return yearsOfEmployment.value * totalGrossSalary.value * factor.value
  })

  // Load initial data
  const loadInitialData = async () => {
    const startOfEmploymentDateValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_LABOR_LAW_BEGINNING_OF_EMPLOYMENT
    )
    // startOfEmploymentDateValue should be in the format DD.MM.YYYY
    // validate the format first
    if (
      !startOfEmploymentDateValue ||
      !/^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.(19|20)\d{2}$/.test(startOfEmploymentDateValue)
    ) {
      startOfEmploymentDate.value = ''
    } else {
      // convert it to the format MM.JJJJ
      const [_startDay, startMonth, startYear] = startOfEmploymentDateValue.split('.')
      startOfEmploymentDate.value = `${startMonth}.${startYear}`
    }

    // do the same for endOfEmploymentDate
    const endOfEmploymentDateValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_LABOR_LAW_END_OF_EMPLOYMENT
    )
    if (
      !endOfEmploymentDateValue ||
      !/^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.(19|20)\d{2}$/.test(endOfEmploymentDateValue)
    ) {
      endOfEmploymentDate.value = ''
    } else {
      // convert it to the format MM.JJJJ
      const [_endDay, endMonth, endYear] = endOfEmploymentDateValue.split('.')
      endOfEmploymentDate.value = `${endMonth}.${endYear}`
    }

    // first attempt to get the base salary using LAW_LABOR_LAW_MONTHLY_GROSS_SALARY
    const baseSalaryValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_LABOR_LAW_MONTHLY_GROSS_SALARY
    )
    try {
      baseSalary.value = Number(baseSalaryValue) || 0
    } catch (e) {
      baseSalary.value = 0
    }

    // if LAW_LABOR_LAW_MONTHLY_GROSS_SALARY has no value, then attempt to
    // update base salary with LAW_LABOR_LAW_YEARLY_GROSS_SALARY
    const yearlyBaseSalaryValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_LABOR_LAW_YEARLY_GROSS_SALARY
    )
    try {
      if (baseSalary.value === 0) {
        baseSalary.value = Number(yearlyBaseSalaryValue) / 12 || 0
      }
    } catch (e) {
      baseSalary.value = 0
    }

    const totalGrossSalaryValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_LABOR_LAW_TOTAL_GROSS_SALARY
    )

    const additionalComponentsValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_LABOR_LAW_SPECIAL_PAYMENTS_AMOUNT
    )
    if (additionalComponentsValue) {
      additionalComponents.value = Number(additionalComponentsValue) / 12
    } else {
      // since base salary + additional component = total gross salary then
      // additional component = total gross salary - base salary
      // however, if total gross salary had a value while base salary had no value
      // it is then safer to just let additionalComponent to have the value 0
      try {
        if (baseSalary.value !== 0) {
          additionalComponents.value = Number(totalGrossSalaryValue) - baseSalary.value
        } else {
          additionalComponents.value = 0
        }
      } catch (e) {
        additionalComponents.value = 0
      }
    }
  }

  // Call loadInitialData when component is mounted
  loadInitialData()

  const { toast } = useToast()

  // Save handler for all fields
  const handleSaveAll = async () => {
    await caseStore.bulkUpdateDataWithMapperKeys({
      [DatamapperKey.LAW_LABOR_LAW_BEGINNING_OF_EMPLOYMENT]: `01.${startOfEmploymentDate.value}`,
      [DatamapperKey.LAW_LABOR_LAW_END_OF_EMPLOYMENT]: `01.${endOfEmploymentDate.value}`,
      [DatamapperKey.LAW_LABOR_LAW_YEARS_OF_EMPLOYMENT]: yearsOfEmployment.value,
      [DatamapperKey.LAW_LABOR_LAW_MONTHLY_GROSS_SALARY]: formatToTwoDecimals(baseSalary.value),
      [DatamapperKey.LAW_LABOR_LAW_YEARLY_GROSS_SALARY]: formatToTwoDecimals(baseSalary.value * 12),
      [DatamapperKey.LAW_LABOR_LAW_SPECIAL_PAYMENTS_AMOUNT]: formatToTwoDecimals(
        additionalComponents.value * 12
      ),
      [DatamapperKey.LAW_LABOR_LAW_TOTAL_GROSS_SALARY]: formatToTwoDecimals(totalGrossSalary.value),
      [DatamapperKey.LAW_LABOR_LAW_SEVERANCE_AMOUNT]: formatToTwoDecimals(severanceSum.value)
    })

    // Track calculator saved event
    await trackEvent(MetricEventType.CALCULATOR_SAVED, {
      calculator_type: 'Severance Payment',
      calculated_sum: severanceSum.value,
      start_employment_date: startOfEmploymentDate.value,
      end_employment_date: endOfEmploymentDate.value,
      years_of_employment: yearsOfEmployment.value,
      base_salary: baseSalary.value,
      additional_components: additionalComponents.value,
      total_gross_salary: totalGrossSalary.value,
      factor: factor.value
    })

    toast({
      title: t('updatedFields'),
      description: t('severanceFieldsUpdated'),
      variant: 'default'
    })
    emit('close')
  }

  // Format function for read-only fields
  const formatToTwoDecimals = (value: number | string | null | undefined): string => {
    // Ensure value is a number before using toFixed
    const numValue = typeof value === 'number' ? value : Number(value || 0)
    return isNaN(numValue) ? '0.00' : numValue.toFixed(2)
  }
</script>

<template>
  <div class="mt-2 flex h-full flex-col overflow-hidden">
    <div class="max-h-[67vh] flex-1 space-y-4 overflow-y-auto">
      <div class="space-y-4">
        <div class="grid grid-cols-2 items-center rounded-md bg-gray-50 p-3">
          <h3 class="col-span-2 pb-1 text-lg font-semibold text-gray-900">
            {{ t('yearsOfEmployment') }}
          </h3>
          <label class="mb-2 block text-sm font-medium">
            {{ t('startOfEmployment') }}
          </label>
          <div class="w-full">
            <InputField
              v-model="startOfEmploymentDate"
              asterisk
              :name="DatamapperKey.LAW_LABOR_LAW_BEGINNING_OF_EMPLOYMENT"
              :rules="
                yup
                  .string()
                  .nullable()
                  .matches(
                    /^(0[1-9]|1[0-2])\.(19|20)\d{2}$/,
                    'Bitte geben Sie ein gültiges Datum im Format MM.JJJJ ein'
                  )
              "
              placeholder="MM.JJJJ"
              data-cy="severanceInput-startOfEmployment"
            />
          </div>
          <label class="mb-2 block text-sm font-medium">
            {{ t('endOfEmployment') }}
          </label>
          <div class="w-full">
            <InputField
              v-model="endOfEmploymentDate"
              :name="DatamapperKey.LAW_LABOR_LAW_TERMINATION_DATE"
              :rules="
                yup
                  .string()
                  .nullable()
                  .matches(
                    /^(0[1-9]|1[0-2])\.(19|20)\d{2}$/,
                    'Bitte geben Sie ein gültiges Datum im Format MM.JJJJ ein'
                  )
              "
              placeholder="MM.JJJJ"
              data-cy="severanceInput-endOfEmployment"
            />
          </div>
          <label class="mb-2 block text-sm font-medium">
            {{ t('yearsOfEmployment') }}
          </label>
          <div class="w-full">
            <InputField
              v-model="yearsOfEmployment"
              name="yearsOfEmployment"
              placeholder="0"
              type="number"
              :disabled="true"
              data-cy="severanceResult-yearsOfEmployment"
            />
          </div>
        </div>
      </div>

      <div class="space-y-4">
        <div class="grid grid-cols-2 items-center rounded-md bg-gray-50 p-3">
          <h3 class="col-span-2 pb-1 text-lg font-semibold text-gray-900">
            {{ t('totalGrossSalary') }}
          </h3>
          <InputField
            v-model="baseSalary"
            asterisk
            suffix="€"
            :label="t('baseSalary')"
            :name="DatamapperKey.LAW_LABOR_LAW_MONTHLY_GROSS_SALARY"
            data-cy="severanceInput-baseSalary"
          />
          <InputField
            v-model="additionalComponents"
            suffix="€"
            :label="t('additionalComponents')"
            name="additionalComponents"
            data-cy="severanceInput-additionalComponents"
          />
          <InputField
            v-model="totalGrossSalary"
            suffix="€"
            :label="t('totalGrossSalary')"
            :disabled="true"
            name="totalGrossSalary"
            data-cy="severanceResult-totalGrossSalary"
          />
        </div>
      </div>

      <div class="space-y-4">
        <div class="grid grid-cols-2 items-center rounded-md bg-gray-50 p-3">
          <h3 class="col-span-2 pb-1 text-lg font-semibold text-gray-900">{{ t('factor') }}</h3>
          <InputField
            v-model="factor"
            asterisk
            :label="t('factor')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_MERANTILE_VALUE_LOSS"
            data-cy="severanceInput-factor"
          />
        </div>
      </div>
    </div>
    <div class="mt-4">
      <h3 class="text-md pb-2 pl-1 font-normal text-gray-900">{{ t('severanceSum') }}</h3>
      <div class="flex items-center gap-4 px-1 pb-2">
        <div class="flex-grow">
          <InputField
            v-model="severanceSum"
            suffix="€"
            :name="DatamapperKey.LAW_LABOR_LAW_TERMINATION_AGREEMENT_SEVERANCE_PAY_AMOUNT"
            :disabled="true"
            data-cy="severanceResult-severanceSum"
          />
        </div>
        <button
          :class="[
            'inline-flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-white',
            'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2',
            isSavingCase ? 'cursor-not-allowed bg-purple-400' : 'bg-purple-600 hover:bg-purple-700',
            'disabled:cursor-not-allowed disabled:opacity-50'
          ]"
          :disabled="isSavingCase"
          data-cy="severanceSaveBtn"
          @click="handleSaveAll"
        >
          <span v-if="isSavingCase">
            <Loader2 class="mr-1 inline h-4 w-4 animate-spin align-text-bottom" />
            {{ t('save') }}
          </span>
          <span v-else>
            {{ t('save') }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>
